// src/pages/task/views/ListView.tsx
// Flat row list with density support and collapsible grouping.
//
// Deliberately not a <table>: a column header strip, cell rules and an outer
// frame made a list of tasks read as a spreadsheet. A row here is a plain flex
// line — hover is the only decoration, the checkbox stays out of the way until
// it is wanted, and the heavier metadata appears as density loosens. The title
// is a real link to the task's own page, so the drawer is a preview rather than
// the only way in.

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import { cn } from '@core/ui';

import type { Task } from '@core/types/task';
import type { TaskStatusDefinition } from '@core/types/task';
import type { DensityMode } from '@/features/workspace/types';

import { StatusSelect } from '@/components/StatusSelect';
import { EntityAvatar } from '@/components/EntityAvatar';
import { PrioritySelect } from '@/components/PrioritySelect';

import {
  getEntityId,
  getTaskStatus,
  getTaskPriority,
  getTaskAssignee,
  getTaskDueDate,
  getTaskProgress,
  getTaskType,
  getTaskTypeInfo,
  getTaskParent,
  getTaskKey,
  getTaskCustomStatus,
  getChecklistProgress,
  getDueDateUrgency,
  getStatusMapping,
  getPriorityColor,
  getPriorityIconPath,
  getDisplayName,
  formatDate,
  taskStatusMap,
  taskPriorityMap,
} from '@/features/task/logic';
import { useParentTitles } from '@/features/task/useParentTitles';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ListViewProps {
  tasks: Task[];
  density: DensityMode;
  groupBy: string | null;
  /** Custom status definitions, for resolving group labels of custom slugs. */
  statusDefinitions?: TaskStatusDefinition[];
  onTaskClick: (id: string) => void;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  onStatusChange?: (taskId: string, status: string) => void;
  onPriorityChange?: (taskId: string, priority: string) => void;
  onDueDateChange?: (taskId: string, date: string) => void;
  onTitleChange?: (taskId: string, title: string) => void;
}

// ---------------------------------------------------------------------------
// Density config
// ---------------------------------------------------------------------------

const DENSITY_ROW_HEIGHTS: Record<DensityMode, string> = {
  compact: 'h-8',
  normal: 'h-11',
  relaxed: 'h-16',
};

const DENSITY_TEXT: Record<DensityMode, string> = {
  compact: 'text-xs',
  normal: 'text-sm',
  relaxed: 'text-sm',
};

// ---------------------------------------------------------------------------
// Grouping helper
// ---------------------------------------------------------------------------

function groupTasks(
  tasks: Task[],
  groupBy: string | null,
): { key: string; label: string; tasks: Task[] }[] {
  if (!groupBy) {
    return [{ key: '__all', label: '', tasks }];
  }

  const groups: Record<string, Task[]> = {};
  const order: string[] = [];

  for (const task of tasks) {
    let value: string;
    switch (groupBy) {
      case 'status':
        // A custom status slug outranks the base enum: grouping must mirror
        // the columns the board shows.
        value = getTaskCustomStatus(task) || getTaskStatus(task) || 'UNKNOWN';
        break;
      case 'priority':
        value = getTaskPriority(task) || 'UNKNOWN';
        break;
      case 'assignee': {
        const assignee = getTaskAssignee(task);
        value = assignee ? getDisplayName(assignee) : 'Unassigned';
        break;
      }
      case 'type':
        value = getTaskType(task) || 'UNKNOWN';
        break;
      case 'parent': {
        const parent = getTaskParent(task);
        value = parent?.id || '__root';
        break;
      }
      default:
        value = 'Other';
    }

    if (!groups[value]) {
      groups[value] = [];
      order.push(value);
    }
    groups[value].push(task);
  }

  return order.map(key => ({
    key,
    label: key,
    tasks: groups[key],
  }));
}

function formatGroupLabel(groupBy: string, key: string, t: (k: string) => string): string {
  if (groupBy === 'status') {
    const mapping = taskStatusMap[key];
    return mapping ? t(mapping.labelKey) : key;
  }
  if (groupBy === 'priority') {
    const mapping = taskPriorityMap[key];
    return mapping ? t(mapping.labelKey) : key;
  }
  return key;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ListView = observer(({
  tasks,
  density,
  groupBy,
  statusDefinitions,
  onTaskClick,
  selectedIds: externalSelectedIds,
  onSelectionChange,
  onStatusChange,
  onPriorityChange,
  onDueDateChange,
  onTitleChange,
}: ListViewProps) => {
  const { t, locale } = useI18n();
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null);

  // Use external selection state when provided, otherwise fall back to internal
  const selectedIds = externalSelectedIds ?? internalSelectedIds;
  const setSelectedIds = onSelectionChange ?? setInternalSelectedIds;

  const groups = useMemo(
    () => groupTasks(tasks, groupBy),
    [tasks, groupBy],
  );

  // Parent titles are resolved whether or not we group by parent: the row
  // indicator's tooltip names the parent too.
  const parents = useMemo(
    () => tasks.flatMap((task) => {
      const parent = getTaskParent(task);
      return parent ? [parent] : [];
    }),
    [tasks],
  );
  const parentTitles = useParentTitles(parents);

  const groupLabel = useCallback(
    (group: { key: string; label: string }): string => {
      if (groupBy === 'parent') {
        if (group.key === '__root') return t('workspace.group.noParent');
        return parentTitles[group.key] || `${group.key.slice(0, 8)}…`;
      }
      if (groupBy === 'status') {
        const definition = statusDefinitions?.find((def) => def.slug === group.key);
        if (definition) return definition.display_name || definition.slug;
      }
      return formatGroupLabel(groupBy!, group.label, t);
    },
    [groupBy, parentTitles, statusDefinitions, t],
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleSelect = useCallback((id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  }, [selectedIds, setSelectedIds]);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === tasks.length) {
      setSelectedIds(new Set<string>());
    } else {
      setSelectedIds(new Set(tasks.map(t => getEntityId(t))));
    }
  }, [selectedIds, tasks, setSelectedIds]);

  const rowHeight = DENSITY_ROW_HEIGHTS[density];
  const textSize = DENSITY_TEXT[density];
  const showKeys = density !== 'compact' && tasks.some((task) => !!getTaskKey(task));

  if (tasks.length === 0) {
    return (
      <p className="px-2 py-8 text-center text-sm text-muted-foreground">
        {t('task.noItems')}
      </p>
    );
  }

  return (
    <div>
      {/* Select-all lives on a quiet strip of its own rather than in a column
          header: it is the one list-wide control the rows cannot carry. */}
      <div className="flex items-center gap-2 px-2 pb-1">
        <input
          type="checkbox"
          checked={selectedIds.size === tasks.length && tasks.length > 0}
          onChange={toggleSelectAll}
          className="h-3.5 w-3.5 rounded border-border"
          aria-label={t('common.selectAll')}
        />
        {selectedIds.size > 0 && (
          <span className="text-xs text-muted-foreground">{selectedIds.size}</span>
        )}
      </div>

      {groups.map(group => (
        <GroupSection
          key={group.key}
          group={group}
          groupBy={groupBy}
          groupLabel={groupLabel(group)}
          parentTitles={parentTitles}
          density={density}
          rowHeight={rowHeight}
          textSize={textSize}
          showKeys={showKeys}
          selectedIds={selectedIds}
          collapsed={collapsedGroups.has(group.key)}
          onToggleGroup={toggleGroup}
          onToggleSelect={toggleSelect}
          onTaskClick={onTaskClick}
          locale={locale}
          t={t}
          editingCell={editingCell}
          setEditingCell={setEditingCell}
          onStatusChange={onStatusChange}
          onPriorityChange={onPriorityChange}
          onDueDateChange={onDueDateChange}
          onTitleChange={onTitleChange}
        />
      ))}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Group section
// ---------------------------------------------------------------------------

interface GroupSectionProps {
  group: { key: string; label: string; tasks: Task[] };
  groupBy: string | null;
  groupLabel: string;
  parentTitles: Record<string, string>;
  density: DensityMode;
  rowHeight: string;
  textSize: string;
  showKeys: boolean;
  selectedIds: Set<string>;
  collapsed: boolean;
  onToggleGroup: (key: string) => void;
  onToggleSelect: (id: string) => void;
  onTaskClick: (id: string) => void;
  locale: string;
  t: (key: string) => string;
  editingCell: { id: string; field: string } | null;
  setEditingCell: (cell: { id: string; field: string } | null) => void;
  onStatusChange?: (taskId: string, status: string) => void;
  onPriorityChange?: (taskId: string, priority: string) => void;
  onDueDateChange?: (taskId: string, date: string) => void;
  onTitleChange?: (taskId: string, title: string) => void;
}

function GroupSection({
  group,
  groupBy,
  groupLabel,
  parentTitles,
  density,
  rowHeight,
  textSize,
  showKeys,
  selectedIds,
  collapsed,
  onToggleGroup,
  onToggleSelect,
  onTaskClick,
  locale,
  t,
  editingCell,
  setEditingCell,
  onStatusChange,
  onPriorityChange,
  onDueDateChange,
  onTitleChange,
}: GroupSectionProps) {
  const showHeader = !!groupBy && group.key !== '__all';

  return (
    <section>
      {/* Group header — sticks to the top of the scroll container so you never
          lose track of which bucket you are reading. */}
      {showHeader && (
        <button
          type="button"
          onClick={() => onToggleGroup(group.key)}
          aria-expanded={!collapsed}
          className={cn(
            'sticky top-0 z-10 flex w-full items-center gap-1.5 border-b border-border/60 px-2 py-1.5 text-left',
            'bg-background/95 backdrop-blur',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <svg
            className={cn(
              'h-3.5 w-3.5 text-muted-foreground transition-transform',
              !collapsed && 'rotate-90',
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-xs font-semibold text-foreground">
            {groupLabel}
          </span>
          <span className="text-xs tabular-nums text-muted-foreground">{group.tasks.length}</span>
        </button>
      )}

      {!collapsed && (
        <ul className={showHeader ? 'pb-2' : undefined}>
          {group.tasks.map(task => (
            <TaskRow
              key={getEntityId(task)}
              task={task}
              density={density}
              rowHeight={rowHeight}
              textSize={textSize}
              showKeys={showKeys}
              parentLabel={(() => {
                const parent = getTaskParent(task);
                return parent?.id ? parentTitles[parent.id] || '' : '';
              })()}
              selected={selectedIds.has(getEntityId(task))}
              onToggleSelect={onToggleSelect}
              onTaskClick={onTaskClick}
              locale={locale}
              t={t}
              editingCell={editingCell}
              setEditingCell={setEditingCell}
              onStatusChange={onStatusChange}
              onPriorityChange={onPriorityChange}
              onDueDateChange={onDueDateChange}
              onTitleChange={onTitleChange}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Task row
// ---------------------------------------------------------------------------

interface TaskRowProps {
  task: Task;
  density: DensityMode;
  rowHeight: string;
  textSize: string;
  showKeys: boolean;
  /** Resolved parent title for the subtask indicator tooltip; '' = top-level. */
  parentLabel: string;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onTaskClick: (id: string) => void;
  locale: string;
  t: (key: string) => string;
  editingCell: { id: string; field: string } | null;
  setEditingCell: (cell: { id: string; field: string } | null) => void;
  onStatusChange?: (taskId: string, status: string) => void;
  onPriorityChange?: (taskId: string, priority: string) => void;
  onDueDateChange?: (taskId: string, date: string) => void;
  onTitleChange?: (taskId: string, title: string) => void;
}

function TaskRow({
  task,
  density,
  rowHeight,
  textSize,
  showKeys,
  parentLabel,
  selected,
  onToggleSelect,
  onTaskClick,
  locale,
  t,
  editingCell,
  setEditingCell,
  onStatusChange,
  onPriorityChange,
  onDueDateChange,
  onTitleChange,
}: TaskRowProps) {
  const id = getEntityId(task);
  const status = getTaskStatus(task);
  const priority = getTaskPriority(task);
  const assignee = getTaskAssignee(task);
  const dueDate = getTaskDueDate(task);
  const progress = getTaskProgress(task);
  const taskType = getTaskType(task);
  const checklist = getChecklistProgress(task);

  const statusInfo = getStatusMapping(taskStatusMap, status);
  const typeInfo = getTaskTypeInfo(taskType);
  const priorityColor = getPriorityColor(priority);
  const priorityIconPath = getPriorityIconPath(priority);
  const dueUrgency = getDueDateUrgency(dueDate, status);
  const assigneeName = assignee ? getDisplayName(assignee) : null;

  const urgencyClass =
    dueUrgency === 'overdue' ? 'text-destructive font-medium' :
    dueUrgency === 'soon' ? 'text-warning font-medium' :
    'text-muted-foreground';

  return (
    <li className="border-b border-border/60 last:border-b-0">
      <div
        className={cn(
          'group relative flex items-center gap-2 px-2',
          rowHeight,
          'cursor-pointer transition-colors hover:bg-muted/50',
          selected && 'bg-primary/5',
        )}
        role="button"
        tabIndex={0}
        aria-label={(task as Task).title}
        onClick={(event) => {
          if (!(event.target as HTMLElement).closest('a, button, input, [role="combobox"]')) onTaskClick(id);
        }}
        onKeyDown={(event) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onTaskClick(id);
          }
        }}
      >
        {/* Checkbox — out of sight until pointed at, focused, or already used */}
        <span
          className={cn(
            'flex w-4 shrink-0 items-center justify-center transition-opacity',
            selected
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100',
          )}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(id)}
            className="h-3.5 w-3.5 rounded border-border accent-primary"
            aria-label={`Select task ${(task as any).title || ''}`}
          />
        </span>

        {/* Priority leads the row: it is the one field that changes what you
            read next, so it sits where the eye lands first. */}
        <span className="flex w-5 shrink-0 justify-center">
          {onPriorityChange ? (
            <PrioritySelect
              value={priority || ''}
              onSelect={(newPriority) => onPriorityChange(id, newPriority)}
              compact
            />
          ) : (
            priorityIconPath ? (
              <svg
                className={`h-4 w-4 ${priorityColor}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-label={priority ? t(getStatusMapping(taskPriorityMap, priority).labelKey) : undefined}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={priorityIconPath} />
              </svg>
            ) : (
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
            )
          )}
        </span>

        {/* The task key is a server-issued number, never a UUID prefix. */}
        {showKeys && (
          <span className={`w-24 shrink-0 font-mono tabular-nums ${textSize} text-muted-foreground`}>
            {getTaskKey(task)}
          </span>
        )}

        {/* Type icon — only when it says something the row does not already */}
        {typeInfo && density === 'relaxed' && (
          <span className="flex w-4 shrink-0 items-center justify-center">
            <svg
              className="h-4 w-4 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-label={t(typeInfo.labelKey)}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d={typeInfo.iconPath} />
            </svg>
          </span>
        )}

        {/* Title + description snippet; subtasks carry a return-arrow glyph
            naming their parent */}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1">
            {getTaskParent(task)?.id && (
              <svg
                className="h-3 w-3 shrink-0 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <title>{parentLabel ? `${t('task.subtaskOf')} ${parentLabel}` : t('task.subtask')}</title>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l-4-4 4-4" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 10h9a5 5 0 015 5v1" />
              </svg>
            )}
            <InlineTitle
              id={id}
              title={(task as any).title || ''}
              description={density !== 'compact' ? (task as any).description : undefined}
              textSize={textSize}
              isEditing={editingCell?.id === id && editingCell?.field === 'title'}
              onStartEdit={onTitleChange ? () => setEditingCell({ id, field: 'title' }) : undefined}
              onSave={onTitleChange ? (newTitle) => { onTitleChange(id, newTitle); setEditingCell(null); } : undefined}
              onCancel={() => setEditingCell(null)}
              onTaskClick={onTaskClick}
              t={t}
            />
          </div>
        </div>

        {/* ── Right-hand metadata, on fixed widths so rows line up ───── */}

        {/* Checklist (relaxed only) */}
        {density === 'relaxed' && (
          <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
            {checklist ? `${checklist.done}/${checklist.total}` : ''}
          </span>
        )}

        {/* Progress bar (relaxed only) */}
        {density === 'relaxed' && (
          <span className="hidden w-24 shrink-0 items-center gap-2 sm:flex">
            {progress > 0 && (
              <>
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
                  <span
                    className="block h-full rounded-full bg-primary transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </span>
                <span className="w-7 text-right text-[10px] tabular-nums text-muted-foreground">{progress}%</span>
              </>
            )}
          </span>
        )}

        {/* Due date (normal, relaxed only) */}
        {density !== 'compact' && (
          <span className="flex w-24 shrink-0 justify-end">
            {editingCell?.id === id && editingCell?.field === 'dueDate' && onDueDateChange ? (
              <InlineDateEditor
                value={dueDate || ''}
                onSave={(newDate) => { onDueDateChange(id, newDate); setEditingCell(null); }}
                onCancel={() => setEditingCell(null)}
              />
            ) : onDueDateChange ? (
              <button
                type="button"
                className={cn(
                  'rounded px-1 py-0.5 text-xs tabular-nums whitespace-nowrap hover:bg-accent',
                  urgencyClass,
                )}
                onClick={() => setEditingCell({ id, field: 'dueDate' })}
                aria-label={t('task.field.dueDate')}
              >
                <DueDateContent dueDate={dueDate} urgency={dueUrgency} locale={locale} />
              </button>
            ) : (
              <span className={`text-xs tabular-nums whitespace-nowrap ${urgencyClass}`}>
                <DueDateContent dueDate={dueDate} urgency={dueUrgency} locale={locale} />
              </span>
            )}
          </span>
        )}

        {/* Status — the one mutable field that earns a permanent slot */}
        <span className="flex w-28 shrink-0 justify-start">
          {onStatusChange ? (
            <StatusSelect
              value={status || ''}
              onSelect={(newStatus) => onStatusChange(id, newStatus)}
              compact
            />
          ) : (
            <Badge variant={statusInfo.variant as any} size="sm">
              {t(statusInfo.labelKey)}
            </Badge>
          )}
        </span>

        {/* Assignee avatar — empty rows stay quiet instead of drawing a
            dashed placeholder for every unassigned task */}
        <span className="flex w-6 shrink-0 justify-center">
          {assigneeName && (
            <EntityAvatar name={assigneeName} image={assignee?.avatar} userId={assignee?.entity?.id} size="xs" className="h-5 w-5 text-[9px]" />
          )}
        </span>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Due date content
// ---------------------------------------------------------------------------

function DueDateContent({
  dueDate,
  urgency,
  locale,
}: {
  dueDate: string | undefined;
  urgency: ReturnType<typeof getDueDateUrgency>;
  locale: string;
}) {
  if (!dueDate) return <>--</>;
  return (
    <>
      {urgency === 'overdue' && (
        <svg className="mr-0.5 -mt-px inline h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      )}
      {formatDate(dueDate, locale)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inline title editor
// ---------------------------------------------------------------------------

interface InlineTitleProps {
  id: string;
  title: string;
  description?: string;
  textSize: string;
  isEditing: boolean;
  onStartEdit?: () => void;
  onSave?: (newTitle: string) => void;
  onCancel: () => void;
  onTaskClick: (id: string) => void;
  t: (key: string) => string;
}

function InlineTitle({
  id,
  title,
  description,
  textSize,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  onTaskClick,
  t,
}: InlineTitleProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(title);

  useEffect(() => {
    if (isEditing) {
      setDraft(title);
      // Focus on next tick after render
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isEditing, title]);

  if (isEditing && onSave) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = draft.trim();
            if (trimmed && trimmed !== title) {
              onSave(trimmed);
            } else {
              onCancel();
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onBlur={() => {
          const trimmed = draft.trim();
          if (trimmed && trimmed !== title) {
            onSave(trimmed);
          } else {
            onCancel();
          }
        }}
        className={`${textSize} font-medium text-foreground bg-background border border-border rounded px-1 py-0.5 w-full outline-none focus:ring-2 focus:ring-ring`}
      />
    );
  }

  return (
    <Link
      to={`/tasks/${id}`}
      className="block min-w-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      /**
       * A plain click previews the task in the drawer, which is what the list is
       * for. ⌘/Ctrl/shift-click and the middle button are left to the browser,
       * so opening a task in a new tab works the way it does for any other link.
       */
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onTaskClick(id);
      }}
      onDoubleClick={
        onStartEdit
          ? (e) => {
              e.preventDefault();
              onStartEdit();
            }
          : undefined
      }
      title={onStartEdit ? t('task.doubleClickToEdit') : undefined}
    >
      <span className={`${textSize} block truncate font-medium text-foreground`}>
        {title || t('common.untitled')}
      </span>
      {description && (
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {description.slice(0, 80)}
        </span>
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Inline date editor
// ---------------------------------------------------------------------------

interface InlineDateEditorProps {
  value: string;
  onSave: (newDate: string) => void;
  onCancel: () => void;
}

function InlineDateEditor({ value, onSave, onCancel }: InlineDateEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  // Click outside detection
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);

  // Convert stored date to input format (YYYY-MM-DD)
  const inputValue = value ? value.slice(0, 10) : '';

  return (
    <div ref={containerRef}>
      <input
        ref={inputRef}
        type="date"
        defaultValue={inputValue}
        onChange={(e) => {
          if (e.target.value) {
            onSave(e.target.value);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const val = (e.target as HTMLInputElement).value;
            if (val) {
              onSave(val);
            } else {
              onCancel();
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onBlur={() => {
          // Small delay to allow click-outside handler to fire first
          setTimeout(() => onCancel(), 150);
        }}
        className="text-xs text-foreground bg-background border border-border rounded px-1 py-0.5 outline-none focus:ring-2 focus:ring-ring"
      />
    </div>
  );
}
